import { LocationResult } from "./types"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { useEffect, useState } from "react"

const iconClasses = "text-gray-100 flex-shrink-0"

export const MapSearchResults = ({
    handleSelect,
    results,
}: {
    handleSelect: (place: LocationResult) => void
    results: LocationResult[]
}) => {
    const [focusedIndex, setFocusedIndex] = useState(0)
    const [hoveringIndex, setHoveringIndex] = useState<number | undefined>(0)

    useEffect(() => {
        setFocusedIndex(0)

        if (results.length === 0) setHoveringIndex(undefined)
    }, [results])

    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowUp":
                    setFocusedIndex((prev) => (prev - 1 + results.length) % results.length)
                    return
                case "ArrowDown":
                    setFocusedIndex((prev) => (prev + 1) % results.length)
                    return
                case "Enter":
                    handleSelect(results[focusedIndex])
                    return
            }
        }

        document.addEventListener("keydown", handleKeydown)

        return () => {
            document.removeEventListener("keydown", handleKeydown)
        }
    }, [results, focusedIndex, handleSelect])

    return results.length > 0 ? (
        <div className="bg-black-800 border border-gray-700 rounded-xl shadow-card px-2 pb-2 pt-3 mb-3">
            <small className="uppercase text-gray-300 mx-2">Locations</small>
            <div className="mt-2">
                {results.map((place, i) => {
                    const isHovering = hoveringIndex === i
                    const isHighlighted =
                        isHovering ||
                        (focusedIndex === i && (hoveringIndex !== undefined ? isHovering : true))

                    return (
                        <div
                            key={`${place.title}-${i}`}
                            className={classNames(
                                "rounded-lg flex p-1.5 gap-2.5 cursor-pointer items-center",
                                {
                                    "bg-gray-900": isHighlighted,
                                }
                            )}
                            onClick={() => handleSelect(place)}
                            onMouseEnter={() => setHoveringIndex(i)}
                            onMouseLeave={() => setHoveringIndex(undefined)}
                        >
                            {place.Icon && (
                                <>
                                    {place.iconSize === "normal" ? (
                                        <Icons.Icon Icon={place.Icon} className={iconClasses} />
                                    ) : (
                                        <Icons.SmallIcon
                                            Icon={place.Icon}
                                            className={iconClasses}
                                        />
                                    )}
                                </>
                            )}
                            <div className="flex flex-col">
                                <h5
                                    className={classNames("break-words", {
                                        "text-gray-0": !isHighlighted,
                                        "text-blue-500": isHighlighted,
                                    })}
                                >
                                    {place.title}
                                </h5>
                                {place.subtitle && (
                                    <small className="text-gray-300 break-words overflow-hidden">
                                        {place.subtitle}
                                    </small>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    ) : null
}
